import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
    streamMessage as apiStreamMessage,
    clearConversation as apiClearConversation,
    restoreConversation as apiRestoreConversation,
    greetUser as apiGreetUser,
} from '@/services/AgentChatService';
import { useApiStore } from '@/stores/api';
import { InterfaceFilterMultipleSeparator } from '@/types/object_classes/InterfaceListingFilter';
import { CleoConversation } from '@/types/ai/cleoConversation';
import type { CleoConversationData } from '@/types/ai/cleoConversation';
import { CleoConversationMessage } from '@/types/ai/cleoConversationMessage';
import type { CleoConversationMessageData, CleoMessageErrorMeta } from '@/types/ai/cleoConversationMessage';
import { CleoConversationMessageTool } from '@/types/ai/cleoConversationMessageTool';
import type { CleoConversationMessageToolData } from '@/types/ai/cleoConversationMessageTool';
import type { TraceStep, ChatMessage, ChatMessageUsage, NavigationOffer, AiProviderError, AiProviderErrorRaw } from '@/types/ai/cleoChat';
import type { TraceEventData, NavigationOfferEventData, UsageEventData, StreamEvent } from '@/services/AgentChatService';

export type ResponseQuota = {
    allowed: boolean;
    limit: number | null;
    used: number;
    remaining: number | null;
    period: string | null;
    reset_at: string | null;
};

export const useCleoStore = defineStore('cleoChat', () => {
    const api = useApiStore();

    const isOpen         = ref(false);
    const isSidePanelOpen = ref(false);
    const messages       = ref<ChatMessage[]>([]);
    const conversationId = ref<string | undefined>(undefined);
    const dbConversationId = ref<number | null>(null);
    const conversationReference = ref<string | null>(null);
    const isReported = ref(false);
    const isSending        = ref(false);
    const isStreaming      = ref(false);
    const isGreeting       = ref(false);
    const viewReloadKey    = ref(0);
    const error            = ref<string | null>(null);
    const mood = ref<'friendly' | 'professional'>('friendly');
    const quota = ref<ResponseQuota | null>(null);
    const draft = ref('');
    const historyIndex = ref<number | null>(null);
    let historyDraftBackup = '';

    const userMessageHistory = (): string[] => {
        return messages.value.filter(m => m.role === 'user' && m.content.trim() !== '').map(m => m.content);
    };

    const historyPrev = (): boolean => {
        const history = userMessageHistory();
        if (history.length === 0) return false;

        if (historyIndex.value === null) {
            historyDraftBackup = draft.value;
            historyIndex.value = history.length - 1;
        } else if (historyIndex.value > 0) {
            historyIndex.value--;
        }

        draft.value = history[historyIndex.value];
        return true;
    };

    const historyNext = (): boolean => {
        if (historyIndex.value === null) return false;

        const history = userMessageHistory();
        if (historyIndex.value >= history.length - 1) {
            historyIndex.value = null;
            draft.value = historyDraftBackup;
            historyDraftBackup = '';
            return true;
        }

        historyIndex.value++;
        draft.value = history[historyIndex.value];
        return true;
    };

    const resetHistoryNavigation = (): void => {
        historyIndex.value = null;
        historyDraftBackup = '';
    };

    const fetchQuota = async (): Promise<void> => {
        try {
            const response = await api.getCustomAction<{ data: ResponseQuota }, any>(
                'cleo-conversations', null, 'checkResponseQuota',
            );
            quota.value = response.data ?? null;
        } catch (e) {
            console.warn('[CleoChat] Could not load response quota:', e);
        }
    };

    const SIDE_PANEL_WIDTH_KEY = 'cleo_side_panel_width';
    const MIN_SIDE_PANEL_WIDTH = 360;
    const maxSidePanelWidth = () => Math.round(window.innerWidth * 0.4);
    const readStoredSidePanelWidth = (): number => {
        const raw = Number(localStorage.getItem(SIDE_PANEL_WIDTH_KEY));
        const w = Number.isFinite(raw) && raw > 0 ? raw : 480;
        return Math.min(Math.max(w, MIN_SIDE_PANEL_WIDTH), maxSidePanelWidth());
    };
    const sidePanelWidth      = ref(readStoredSidePanelWidth());
    const isResizingSidePanel = ref(false);
    const clampSidePanelWidth = (w: number): number => {
        return Math.min(Math.max(w, MIN_SIDE_PANEL_WIDTH), maxSidePanelWidth());
    };
    const persistSidePanelWidth = (): void => {
        localStorage.setItem(SIDE_PANEL_WIDTH_KEY, String(sidePanelWidth.value));
    };

    const toggle = () => { isOpen.value = !isOpen.value; };
    const close  = () => { isOpen.value = false; };

    const openSidePanel  = () => { isOpen.value = false; isSidePanelOpen.value = true; };
    const closeSidePanel = () => { isSidePanelOpen.value = false; };

    const persistErrorMessage = async (
        conversationDbId: number,
        err: AiProviderError,
    ): Promise<void> => {
        const errorMsg = new CleoConversationMessage();
        errorMsg.cleo_conversation_id = conversationDbId;
        errorMsg.role = 'error';
        errorMsg.content = err.message;
        errorMsg.error_code = err.code;
        errorMsg.error_meta = {
            retryable: err.retryable,
            retry_after_seconds: err.retryAfterSeconds ?? null,
            raw: err.raw ?? null,
        };
        try {
            await api.createItem<CleoConversationMessage, CleoConversationMessageData>(
                'cleo-conversation-messages', errorMsg, CleoConversationMessage, null,
            );
        } catch (e) {
            console.error('[CleoChat] Failed to save error message:', e);
        }
    };

    const createConversationRecord = async (): Promise<void> => {
        try {
            const newConv = new CleoConversation();
            newConv.mood = mood.value;
            const conv = await api.createItem<CleoConversation, CleoConversationData>(
                'cleo-conversations', newConv, CleoConversation, null,
            );
            dbConversationId.value = conv.id ?? null;
            conversationReference.value = conv.reference ?? null;
            isReported.value = false;
        } catch (e) {
            console.error('[CleoChat] Failed to create conversation record:', e);
        }
    };

    const saveUserMessage = async (conversationDbId: number, content: string): Promise<void> => {
        const userMsg = new CleoConversationMessage();
        userMsg.cleo_conversation_id = conversationDbId;
        userMsg.role = 'user';
        userMsg.content = content;
        try {
            await api.createItem<CleoConversationMessage, CleoConversationMessageData>(
                'cleo-conversation-messages', userMsg, CleoConversationMessage, null,
            );
        } catch (e) {
            console.error('[CleoChat] Failed to save user message:', e);
        }
    };

    const saveToolTrace = async (messageId: number | null, step: TraceStep, sortOrder: number): Promise<void> => {
        const tool = new CleoConversationMessageTool();
        tool.cleo_conversation_message_id = messageId;
        tool.event = step.event;
        tool.plugin = step.plugin;
        tool.function_name = step.function;
        tool.args = step.args ? (step.args as Record<string, string>) : null;
        tool.result = step.result ?? null;
        tool.error = step.error ?? null;
        tool.sort_order = sortOrder;
        try {
            await api.createItem<CleoConversationMessageTool, CleoConversationMessageToolData>(
                'cleo-conversation-message-tools', tool, CleoConversationMessageTool, null,
            );
        } catch (e) {
            console.error('[CleoChat] Failed to save tool call (event=' + step.event + '):', e);
        }
    };

    const persistAssistantMessage = async (
        conversationDbId: number,
        content: string,
        usage: ChatMessageUsage | null,
        traceSteps: TraceStep[],
    ): Promise<number | null> => {
        const assistantMsg = new CleoConversationMessage();
        assistantMsg.cleo_conversation_id = conversationDbId;
        assistantMsg.role = 'assistant';
        assistantMsg.content = content;
        if (usage) {
            assistantMsg.model_name    = usage.model;
            assistantMsg.input_tokens  = usage.input_tokens;
            assistantMsg.output_tokens = usage.output_tokens;
            assistantMsg.cached_tokens = usage.cached_tokens;
        }
        try {
            const saved = await api.createItem<CleoConversationMessage, CleoConversationMessageData>(
                'cleo-conversation-messages', assistantMsg, CleoConversationMessage, null,
            );
            await Promise.all(traceSteps.map((step, index) => saveToolTrace(saved.id ?? null, step, index)));
            return saved.cost !== null && saved.cost !== undefined ? Number(saved.cost) : null;
        } catch (e) {
            console.error('[CleoChat] Failed to save assistant message:', e);
            return null;
        }
    };

    const reportProblem = async (reason: string | null): Promise<boolean> => {
        if (dbConversationId.value === null) return false;
        try {
            await api.postCustomAction('cleo-conversations', dbConversationId.value, 'reportProblem', { flag_reason: reason });
            isReported.value = true;
            return true;
        } catch (e) {
            console.error('[CleoChat] Failed to report a problem with the conversation:', e);
            return false;
        }
    };

    const saveConversationTitle = async (conversationDbId: number, title: string): Promise<void> => {
        try {
            await api.postCustomAction('cleo-conversations', conversationDbId, 'setTitle', { title });
        } catch (e) {
            console.warn('[CleoChat] Could not set conversation title:', e);
        }
    };

    const buildRestoreHistory = (): Array<{ role: 'user' | 'assistant'; content: string }> => {
        return messages.value
            .slice(0, -1)
            .filter(m => m.role === 'user' || m.role === 'agent')
            .map(m => ({
                role: (m.role === 'agent' ? 'assistant' : 'user') as 'user' | 'assistant',
                content: m.content,
            }));
    };

    const buildProviderError = (event: StreamEvent): AiProviderError => {
        return {
            code:              event.code ?? 'unknown',
            message:           event.message ?? 'An error occurred while contacting the AI assistant.',
            retryable:         event.retryable ?? false,
            retryAfterSeconds: event.retry_after_seconds ?? null,
            raw:               event.raw,
        };
    };

    const formatQuotaReset = (resetAtIso: string): string => {
        const parsed = new Date(resetAtIso);
        if (Number.isNaN(parsed.getTime())) return '';

        const resetDate = new Date(parsed.getTime() + 1000);
        const now       = new Date();
        const tomorrow  = new Date(now);
        tomorrow.setDate(now.getDate() + 1);

        const time = resetDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        if (resetDate.toDateString() === now.toDateString()) return `today at ${time}`;
        if (resetDate.toDateString() === tomorrow.toDateString()) return `tomorrow at ${time}`;

        const date = resetDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        return `on ${date} at ${time}`;
    };

    const buildQuotaError = (event: StreamEvent): AiProviderError => {
        const periodWord  = event.period === 'week' ? 'weekly' : event.period === 'month' ? 'monthly' : 'daily';
        const limitPart   = event.limit != null ? ` of ${event.limit} response${event.limit === 1 ? '' : 's'}` : '';
        const resetPhrase = event.reset_at ? formatQuotaReset(event.reset_at) : '';
        const resetPart   = resetPhrase
            ? ` You'll be able to chat with Cleo again ${resetPhrase}.`
            : ' Please try again later.';
        return {
            code:              'quota_exceeded',
            message:           `You've reached your ${periodWord} Cleo limit${limitPart}.${resetPart}`,
            retryable:         false,
            retryAfterSeconds: null,
        };
    };

    const send = async (text: string) => {
        const userText = text.trim();
        if (!userText || isSending.value) return;

        resetHistoryNavigation();
        error.value = null;
        isSending.value = true;
        isStreaming.value = false;

        const isNewConversation = dbConversationId.value === null;
        if (isNewConversation) await createConversationRecord();

        messages.value.push({ role: 'user', content: userText, timestamp: new Date() });
        if (dbConversationId.value !== null) await saveUserMessage(dbConversationId.value, userText);

        let agentMsgIdx = -1;
        let providerError: AiProviderError | null = null;
        let currentUsage: ChatMessageUsage | null = null;
        let generatedTitle: string | null = null;

        const ensureAgentMsg = () => {
            if (agentMsgIdx === -1) {
                agentMsgIdx = messages.value.length;
                messages.value.push({ role: 'agent', content: '', timestamp: new Date(), traceSteps: [] });
            }
        };

        try {
            let streamConvId   = conversationId.value;
            let threadRestored = false;
            let needsRetry     = true;

            while (needsRetry) {
                needsRetry = false;

                for await (const event of apiStreamMessage(userText, streamConvId, mood.value, isNewConversation)) {
                    if (event.type === 'start' && event.conversationId) {
                        streamConvId         = event.conversationId;
                        conversationId.value = event.conversationId;
                    } else if (event.type === 'thread_expired' && !threadRestored) {
                        try {
                            streamConvId         = await apiRestoreConversation(buildRestoreHistory());
                            conversationId.value = streamConvId;
                        } catch (e) {
                            console.warn('[CleoChat] Could not restore thread after expiry — retrying without context:', e);
                            streamConvId         = undefined;
                            conversationId.value = undefined;
                        }
                        threadRestored = true;
                        needsRetry     = true;
                        break;
                    } else if (event.type === 'trace' && event.data) {
                        ensureAgentMsg();
                        const traceData = event.data as TraceEventData;
                        if (traceData.event === 'tool_call') {
                            const pendingThought = messages.value[agentMsgIdx].content.trim();
                            if (pendingThought) {
                                messages.value[agentMsgIdx].traceSteps!.push({
                                    event:    'thinking',
                                    plugin:   'Orchestrator',
                                    function: 'Thinking',
                                    result:   pendingThought,
                                });
                                messages.value[agentMsgIdx].content = '';
                            }
                        }
                        messages.value[agentMsgIdx].traceSteps!.push({
                            event:    traceData.event,
                            plugin:   traceData.plugin,
                            function: traceData.function,
                            args:     traceData.args,
                            result:   traceData.result,
                            error:    traceData.error,
                        });
                    } else if (event.type === 'chunk' && event.content) {
                        ensureAgentMsg();
                        isStreaming.value = true;
                        messages.value[agentMsgIdx].content += event.content;
                    } else if (event.type === 'navigation_offer' && event.data) {
                        ensureAgentMsg();
                        const offerData = event.data as NavigationOfferEventData;
                        messages.value[agentMsgIdx].navigationOffers = (offerData.options ?? []) as NavigationOffer[];
                    } else if (event.type === 'usage' && event.data) {
                        ensureAgentMsg();
                        const usageData = event.data as UsageEventData;
                        currentUsage = {
                            model:         usageData.model,
                            input_tokens:  usageData.input_tokens,
                            output_tokens: usageData.output_tokens,
                            cached_tokens: usageData.cached_tokens,
                            cost:          null,
                        };
                        messages.value[agentMsgIdx].usage = currentUsage;
                    } else if (event.type === 'ai_provider_error') {
                        ensureAgentMsg();
                        providerError = buildProviderError(event);
                        messages.value[agentMsgIdx].error = providerError;
                    } else if (event.type === 'quota_exceeded') {
                        ensureAgentMsg();
                        providerError = buildQuotaError(event);
                        messages.value[agentMsgIdx].error = providerError;
                        quota.value = {
                            allowed:   false,
                            limit:     event.limit ?? null,
                            used:      event.used ?? quota.value?.used ?? 0,
                            remaining: 0,
                            period:    event.period ?? quota.value?.period ?? null,
                            reset_at:  event.reset_at ?? null,
                        };
                    } else if (event.type === 'title' && event.title) {
                        generatedTitle = event.title;
                    } else if (event.type === 'done') {
                        break;
                    }
                }
            }

            isSending.value = false;
            isStreaming.value = false;

            if (dbConversationId.value === null) {
                console.warn('[CleoChat] Skipping assistant/error save — conversation creation failed.');
            } else if (agentMsgIdx !== -1) {
                const assistantContent = messages.value[agentMsgIdx]?.content ?? '';
                const traceSteps       = messages.value[agentMsgIdx]?.traceSteps ?? [];

                if (assistantContent) {
                    const cost = await persistAssistantMessage(dbConversationId.value, assistantContent, currentUsage, traceSteps);
                    if (currentUsage && cost !== null) {
                        currentUsage.cost = cost;
                        messages.value[agentMsgIdx].usage = { ...currentUsage };
                    }
                }

                if (providerError) {
                    await persistErrorMessage(dbConversationId.value, providerError);
                }
            }

            if (isNewConversation && dbConversationId.value !== null && generatedTitle) {
                await saveConversationTitle(dbConversationId.value, generatedTitle);
            }
        } catch (err: any) {
            const fallback: AiProviderError = {
                code: 'network_error',
                message: 'Could not reach the AI assistant. Please try again.',
                retryable: true,
                retryAfterSeconds: null,
            };
            ensureAgentMsg();
            messages.value[agentMsgIdx].error = fallback;
            error.value = fallback.message;

            if (dbConversationId.value !== null) {
                await persistErrorMessage(dbConversationId.value, fallback);
            }
        } finally {
            isSending.value = false;
            isStreaming.value = false;
            void fetchQuota();
        }
    };

    const retryLastUserMessage = async (): Promise<void> => {
        if (isSending.value) return;

        let lastUserIdx = -1;
        for (let i = messages.value.length - 1; i >= 0; i--) {
            if (messages.value[i].role === 'user') {
                lastUserIdx = i;
                break;
            }
        }
        if (lastUserIdx === -1) return;
        const lastUserText = messages.value[lastUserIdx].content;

        messages.value.splice(lastUserIdx);

        await send(lastUserText);
    };

    const loadConversation = async (conv: CleoConversation) => {
        if (conversationId.value) {
            try {
                await apiClearConversation(conversationId.value);
            } catch {
            }
        }
        resetHistoryNavigation();

        messages.value         = [];
        conversationId.value   = undefined;
        dbConversationId.value = conv.id;
        conversationReference.value = conv.reference ?? null;
        isReported.value       = false;
        mood.value             = conv.mood ?? 'friendly';
        error.value            = null;

        try {
            const convIdFilter = [{ field: 'cleo_conversation_id', delimiter: InterfaceFilterMultipleSeparator.SEPARATOR, values: [String(conv.id)] }];
            const dbMsgs = await api.getItems<CleoConversationMessage, CleoConversationMessageData>(
                'cleo-conversation-messages',
                CleoConversationMessage,
                ['tools'],
                undefined,
                convIdFilter,
                { page_number: 1, page_size: 200 },
                ['id'],
            );

            const toolsByMsgId = new Map<number, CleoConversationMessageTool[]>();
            for (const m of dbMsgs) {
                if (m.id !== null && (m.tools?.length ?? 0) > 0) {
                    toolsByMsgId.set(m.id, [...m.tools!].sort((a, b) => {
                        const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
                        const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
                        if (ao !== bo) return ao - bo;
                        return (a.id ?? 0) - (b.id ?? 0);
                    }));
                }
            }

            messages.value = dbMsgs
                .filter(m => m.role === 'user' || m.role === 'assistant' || m.role === 'error')
                .map(m => {
                    if (m.role === 'error') {
                        const meta = (m.error_meta ?? {}) as CleoMessageErrorMeta;
                        const errorPayload: AiProviderError = {
                            code: m.error_code ?? 'unknown',
                            message: m.content,
                            retryable: meta.retryable ?? false,
                            retryAfterSeconds: meta.retry_after_seconds ?? null,
                            raw: (meta.raw ?? undefined) as AiProviderErrorRaw | undefined,
                        };
                        return {
                            role: 'agent' as const,
                            content: '',
                            timestamp: m.created_at ?? new Date(),
                            error: errorPayload,
                        };
                    }
                    const tools = m.id !== null ? (toolsByMsgId.get(m.id) ?? []) : [];
                    const traceSteps: TraceStep[] = tools.map(t => ({
                        event: t.event as TraceStep['event'],
                        plugin: t.plugin,
                        function: t.function_name,
                        args: t.args ?? undefined,
                        result: t.result ?? undefined,
                        error: t.error ?? undefined,
                    }));
                    const usage: ChatMessageUsage | undefined = (m.role === 'assistant' && m.input_tokens !== null && m.model_name !== null)
                        ? {
                            model:         m.model_name,
                            input_tokens:  m.input_tokens ?? 0,
                            output_tokens: m.output_tokens ?? 0,
                            cached_tokens: m.cached_tokens ?? 0,
                            cost:          m.cost !== null ? Number(m.cost) : null,
                        }
                        : undefined;
                    return {
                        role: (m.role === 'user' ? 'user' : 'agent') as 'user' | 'agent',
                        content: m.content,
                        timestamp: m.created_at ?? new Date(),
                        traceSteps: traceSteps.length > 0 ? traceSteps : undefined,
                        usage,
                    };
                });

            const historyForAgent = dbMsgs
                .filter(m => m.role === 'user' || m.role === 'assistant')
                .map(m => ({
                    role: m.role as 'user' | 'assistant',
                    content: m.content,
                }));
            try {
                conversationId.value = await apiRestoreConversation(historyForAgent);
            } catch (e) {
                console.warn('[CleoChat] Could not restore agent thread (tools may not work):', e);
            }
        } catch (e) {
            console.error('[CleoChat] Failed to load conversation messages:', e);
        }
    };

    const greet = async () => {
        if (isGreeting.value || isSending.value) return;
        isGreeting.value = true;
        try {
            const response = await apiGreetUser(mood.value);
            conversationId.value = response.conversationId;
            messages.value.push({
                role: 'agent',
                content: response.message,
                timestamp: new Date(),
            });
        } catch (e) {
            console.warn('[CleoChat] Could not load greeting:', e);
        } finally {
            isGreeting.value = false;
        }
    };

    const reset = async () => {
        if (conversationId.value) {
            try {
                await apiClearConversation(conversationId.value);
            } catch {
            }
        }
        resetHistoryNavigation();
        messages.value         = [];
        conversationId.value   = undefined;
        dbConversationId.value = null;
        conversationReference.value = null;
        isReported.value       = false;
        error.value            = null;

        await greet();
    };

    const bumpViewReloadKey = (): void => {
        viewReloadKey.value++;
    };

    return {
        isOpen,
        isSidePanelOpen,
        messages,
        conversationId,
        dbConversationId,
        conversationReference,
        isReported,
        reportProblem,
        isSending,
        isStreaming,
        isGreeting,
        error,
        mood,
        quota,
        fetchQuota,
        draft,
        historyPrev,
        historyNext,
        viewReloadKey,
        sidePanelWidth,
        isResizingSidePanel,
        clampSidePanelWidth,
        persistSidePanelWidth,
        toggle,
        close,
        openSidePanel,
        closeSidePanel,
        send,
        retryLastUserMessage,
        greet,
        reset,
        loadConversation,
        bumpViewReloadKey,
    };
});
